/**
 * @fileoverview アプリケーションのコントローラー(司令塔)です。
 * アプリケーション全体の初期化、UIイベントの監視、ユーザー操作への応答、
 * および各モジュール(State, Api, UI)の連携を担当します。
 */
"use strict";

import { Session, AuthChangeEvent } from "@supabase/supabase-js";

import { Api } from "./api";
import { Constants, supabaseClient } from "./constants";
import State from "./state";
import { isAppError } from "./types/api-error";
import type { AppError } from "./types/api-error";
import type { ApiPayload, ApiResponse } from "./types/api.d";
import {
  QuestionEditData,
  UpdateQuestionRequest,
} from "./types/edit-question.d";
import { ExamData } from "./types/exam.d";
import { UI } from "./ui";

export const App = {
  /**
   * アプリケーションを初期化します。
   * DOM要素のキャッシュ、イベントリスナーの設定、認証状態の監視を開始します。
   */
  init: function (): void {
    UI.cacheDomElements();
    UI.setupEventListeners(); // UIのイベントリスナーを設定
    // 初期状態では問題編集メニューを非表示にする
    UI.toggleElementVisibility(UI.elements.editQuestionMenuItem, false);
    this.bindEvents();
    this.setupAuthListener(); // 認証状態の監視を最初に開始
    UI.populateQuizLengthOptions();
    UI.populateQuizAlgorithmOptions();
    UI.populateQuizModeOptions();
    document.title = Constants.APP_CONFIG.APP_TITLE;
    this.setFooterYear();
  },

  /**
   * アプリケーション内の主要なUIイベントリスナーをすべて設定します。
   */
  bindEvents: function (): void {
    // クイズ画面のボタン
    UI.elements.answerBtn.addEventListener(
      "click",
      this.submitAnswer.bind(this)
    );
    UI.elements.nextQuestionBtn.addEventListener(
      "click",
      (event: MouseEvent) => {
        event.stopPropagation();
        this.loadNextQuestion();
      }
    );
    UI.elements.showResultBtn.addEventListener(
      "click",
      this.displayResults.bind(this)
    );
    UI.elements.restartQuizBtn.addEventListener(
      "click",
      this.restartQuiz.bind(this)
    );

    // 試験選択画面のオプション
    UI.elements.quizLengthSelect.addEventListener(
      "change",
      this.handleQuizLengthChange.bind(this)
    );
    UI.elements.quizLengthOptionsContainer.addEventListener(
      "change",
      this.handleQuizLengthChange.bind(this)
    );
    UI.elements.quizAlgorithmSelect.addEventListener(
      "change",
      this.handleQuizAlgorithmChange.bind(this)
    );
    UI.elements.quizAlgorithmOptionsContainer.addEventListener(
      "change",
      this.handleQuizAlgorithmChange.bind(this)
    );
    UI.elements.quizModeSelect.addEventListener(
      "change",
      this.handleQuizModeChange.bind(this)
    );
    UI.elements.quizModeOptionsContainer.addEventListener(
      "change",
      this.handleQuizModeChange.bind(this)
    );

    // クイズ中のインタラクション
    UI.elements.optionsList.addEventListener(
      "click",
      this.handleOptionClick.bind(this)
    );

    // ダイアログ
    UI.elements.customConfirmOkBtn.addEventListener(
      "click",
      this.handleDialogOk.bind(this)
    );
    UI.elements.customConfirmCancelBtn.addEventListener(
      "click",
      this.handleDialogCancel.bind(this)
    );

    // アコーディオン (イベント委譲)
    UI.elements.examAccordionContainer.addEventListener(
      "click",
      this.handleAccordionClick.bind(this)
    );

    // ログインボタン (メインコンテンツエリア)
    if (UI.elements.googleLoginButton)
      UI.elements.googleLoginButton.addEventListener(
        "click",
        this.handleLogin.bind(this)
      );
    // ログアウトボタン (設定メニュー内)
    if (UI.elements.logoutButton)
      UI.elements.logoutButton.addEventListener(
        "click",
        this.handleLogout.bind(this)
      );

    // 問題編集ボタン (設定メニュー内)
    if (UI.elements.editQuestionMenuItem) {
      UI.elements.editQuestionMenuItem.addEventListener(
        "click",
        this.handleEditQuestionClick.bind(this)
      );
    }

    // ダイアログ外クリックで閉じる
    document.addEventListener("click", (event: MouseEvent) => {
      const dialog = UI.elements.customConfirmDialog;
      const dialogContent = UI.elements.customDialogContent;

      // ダイアログが表示されている場合のみ処理
      if (dialog && !dialog.classList.contains(Constants.CSS_CLASSES.HIDDEN)) {
        // 問題修正中の場合は、背景クリックで閉じないようにする
        if (State.editingQuestionId) return;

        const target = event.target as HTMLElement;

        // クリックがダイアログのコンテンツ外であった場合
        if (dialogContent && !dialogContent.contains(target)) {
          // キャンセルコールバックがあれば実行
          if (State._currentDialogCancelCallback) {
            State._currentDialogCancelCallback();
          }
          UI.hideCustomConfirm();
        }
      }
    });
  },

  /**
   * Supabaseの認証状態の変化(ログイン、ログアウト)を監視し、UIを動的に更新します。
   */
  setupAuthListener: function (): void {
    let isInitialLoadComplete = false; // 初期読み込みが完了したかを管理するフラグ

    supabaseClient.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        // ログイン状態

        if (session && session.user) {
          // UIの基本的な表示状態は毎回更新
          UI.toggleElementVisibility(UI.elements.loginArea, false);
          UI.toggleElementVisibility(UI.elements.loggedInView, true);
          UI.toggleElementVisibility(UI.elements.settingsButton, true);
          UI.updateUserInfo(session.user.email || "");
          State.currentUser = session.user;

          // 権限を確認してStateを更新
          Api.checkUserPermission().then((hasPermission) => {
            State.hasEditPermission = hasPermission;
            // 権限がない場合は、メニュー項目を即座に非表示にする（念のため）
            if (!hasPermission) {
              UI.toggleElementVisibility(
                UI.elements.editQuestionMenuItem,
                false
              );
            }
          });

          // まだ初期読み込みが完了していない場合のみ、試験一覧を読み込む
          if (!isInitialLoadComplete) {
            // TOKEN_REFRESHED は読み込みのトリガーにしない
            if (event !== "TOKEN_REFRESHED") {
              isInitialLoadComplete = true; // 読み込みを実行するのでフラグを立てる
              App.loadAndDisplayExamSelection();
            }
          }
        } else {
          // ログアウト状態
          isInitialLoadComplete = false; // ログアウトしたらフラグをリセット
          this.forceLogoutUI();
        }
      }
    );
  },

  /**
   * Google OAuthによるログイン処理を開始します。
   */
  handleLogin: async function (): Promise<void> {
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options: {
        // 認証後、現在のページにリダイレクトして戻ってくる
        redirectTo: window.location.href,
      },
    });
    if (error) {
      console.error("Googleログイン処理でエラー:", error.message);
      this.handleError(error, "ログイン中にエラーが発生しました");
    }
  },

  /**
   * ログアウト処理を実行し、UIを強制的にログアウト状態にします。
   */
  handleLogout: async function (): Promise<void> {
    const { error } = await supabaseClient.auth.signOut();
    if (error) {
      console.error("ログアウト処理でエラー:", error.message);
    }
    // signOutの成否に関わらず、UIを強制的にログアウト状態にする
    this.forceLogoutUI();
  },

  /**
   * UIを強制的にログアウト状態にし、ログインプロンプトを表示します。
   */
  forceLogoutUI: function (): void {
    UI.toggleElementVisibility(UI.elements.loginArea, true);
    UI.toggleElementVisibility(UI.elements.loggedInView, false);
    UI.toggleElementVisibility(UI.elements.settingsButton, false);
    UI.toggleElementVisibility(UI.elements.settingsMenu, false);
    UI.updateUserInfo("");
    State.currentUser = null;
    State.hasEditPermission = false; // 権限リセット
    UI.displayLoginPrompt();
  },

  /**
   * 試験一覧をAPIから非同期で読み込み、UIを構築して表示します。
   * UI表示後、バックグラウンドで最初の試験の問題データをプリフェッチします。
   */
  loadAndDisplayExamSelection: async function (): Promise<void> {
    UI.showLoading(true);
    try {
      const allExamData = await Api.getExamNames();
      // 表示設定がONのものだけを抽出（ダッシュボード表示用）
      const visibleExams = allExamData.filter((exam) => exam.is_user_visible);

      UI.buildExamAccordion(visibleExams);
      UI.showArea("examSelection");

      // UI表示と並行して、表示対象の最初の試験データをプリフェッチする
      if (visibleExams.length > 0) {
        const firstExam = visibleExams[0];
        // プリフェッチ済みでなければ実行
        if (!State.prefetchedQuestions[firstExam.examId]) {
          this.prefetchQuizData(firstExam.examId);
        }
      }
    } catch (error: unknown) {
      this.handleError(error, "試験情報リストの取得に失敗しました");
    } finally {
      UI.showLoading(false);
    }
  },

  /**
   * 指定された試験の問題データをバックグラウンドで先読み（プリフェッチ）します。
   * ユーザーがその試験をすぐに開始した場合の待ち時間をなくすことが目的です。
   * @param {number} examId - プリフェッチする試験のID。
   */
  prefetchQuizData: async function (examId: number): Promise<void> {
    try {
      // プリフェッチは最も一般的な設定（バランスモード、最大問題数）で行う
      const prefetchQuizLength = Math.max(
        ...Constants.APP_CONFIG.QUIZ_LENGTH_OPTIONS
      );
      const prefetchAlgorithm = Constants.APP_CONFIG.QUIZ_ALGORITHMS.BALANCE;

      console.log(`[プリフェッチ] 試験ID: ${examId} の先読みを開始します。`);
      const response = await Api.getQuizData(
        examId,
        prefetchQuizLength,
        prefetchAlgorithm,
        null // Pass null for areas during prefetch
      );

      const questions = response.questions;
      if (questions && questions.length > 0) {
        State.prefetchedQuestions[examId] = questions;
        console.log(
          `[プリフェッチ] 試験ID: ${examId} の問題 ${questions.length} 件を先読みしました。`
        );
      }
    } catch (error: unknown) {
      // プリフェッチの失敗はユーザーに通知しない（あくまでバックグラウンド処理のため）
      console.error(
        `[プリフェッチ] 試験ID: ${examId} のデータ先読みに失敗しました:`,
        error
      );
    }
  },

  /**
   * 問題演習を開始するメインロジック。
   * プリフェッチされたデータがあればそれを使用し、なければAPIから取得します。
   * @param {number} examId - 開始する試験のID。
   */
  startQuiz: async function (examId: number): Promise<void> {
    if (!State.currentUser) {
      UI.showCustomInfoDialog(
        "ログインが必要です",
        "クイズを開始するには、Googleアカウントでログインしてください。"
      );
      return;
    }

    // Determine prefetch strategy and get selected areas BEFORE resetting state
    const usePrefetched =
      State.selectedAreaIds.length === 0 && // Use prefetch ONLY if no specific areas are selected
      State.prefetchedQuestions[examId] &&
      State.selectedQuizAlgorithm ===
        Constants.APP_CONFIG.QUIZ_ALGORITHMS.BALANCE;

    const areaIdsToFetch =
      State.selectedAreaIds.length > 0 ? State.selectedAreaIds : null;

    // Now it's safe to reset the state for the new quiz session
    State.resetQuizState();

    // 領域リストをバックグラウンドで取得・キャッシュ (編集機能の高速化用)
    Api.getExamAreas(examId)
      .then((res) => {
        State.currentExamAreas = res.availableAreas;
        console.log(
          `[領域キャッシュ] ${res.availableAreas.length}件の領域をキャッシュしました。`
        );
      })
      .catch((err) =>
        console.warn("[領域キャッシュ] 取得に失敗しました:", err)
      );

    State.sessionId = crypto.randomUUID();
    console.log(
      `[クイズ開始] 新規セッションを開始しました: ${State.sessionId}`
    );

    const loadQuestions = async () => {
      const questions = State.currentQuestions;
      if (!questions || questions.length === 0) {
        throw new Error("有効な問題データがありません。");
      }

      UI.showArea("quiz");
      const question = State.getCurrentQuestion();
      if (!question) return; // Should not happen

      let finalDisplayMode: string;
      if (State.selectedQuizMode === "adaptive") {
        finalDisplayMode = question.display_mode || "normal";
      } else {
        finalDisplayMode = State.selectedQuizMode;
      }
      State.currentQuestionDisplayMode = finalDisplayMode;
      UI.displayQuestion(finalDisplayMode);
    };

    if (usePrefetched) {
      console.log(
        `[クイズ開始] プリフェッチされたデータを利用します。試験ID: ${examId}`
      );
      const allQuestions = State.prefetchedQuestions[examId];
      State.currentQuestions = allQuestions.slice(
        0,
        Math.min(State.selectedQuizLength, allQuestions.length)
      );
      await loadQuestions();
    } else {
      console.log(
        `[クイズ開始] サーバーからデータを取得します。試験ID: ${examId}, アルゴリズム: ${State.selectedQuizAlgorithm}, 領域ID: ${State.selectedAreaIds.join(
          ", "
        )}`
      );
      UI.showLoading(true);
      try {
        const response = await Api.getQuizData(
          examId,
          State.selectedQuizLength,
          State.selectedQuizAlgorithm,
          areaIdsToFetch // Pass the preserved selection
        );
        State.currentQuestions = response.questions;
        await loadQuestions();
      } catch (error: unknown) {
        this.handleError(error, `問題の取得に失敗しました`);
        this.restartQuiz();
      } finally {
        UI.showLoading(false);
      }
    }
  },

  /**
   * ユーザーの解答を処理します。
   * 正誤判定、UI更新、およびAPI経由での解答記録を行います。
   */
  submitAnswer: function (): void {
    const selectedRadio = UI.elements.optionsList.querySelector(
      'input[name="quiz_option"]:checked'
    ) as HTMLInputElement | null;
    if (!selectedRadio) {
      UI.showCustomInfoDialog("エラー", "選択肢を選んでください。");
      return;
    }
    (UI.elements.answerBtn as HTMLButtonElement).disabled = true;
    UI.toggleElementVisibility(UI.elements.answerBtn, false);

    const question = State.getCurrentQuestion();
    if (!question || question.area_id === null) {
      console.error("現在の問題または領域IDが見つかりません。");
      return;
    }

    let answerTimeMs: number;
    let recallTimeMs: number | undefined = undefined;

    if (
      State.currentQuestionDisplayMode ===
      Constants.APP_CONFIG.QUIZ_MODES.FLASHCARD
    ) {
      // フラッシュカードモードの場合
      if (State.questionStartTime && State.recallPhaseEndTime) {
        recallTimeMs = State.recallPhaseEndTime - State.questionStartTime;
        answerTimeMs = Date.now() - State.recallPhaseEndTime;
      } else {
        console.warn(
          "フラッシュカードモードで時間計測に必要な情報が不足しています。デフォルト値を使用します。"
        );
        recallTimeMs = 0;
        answerTimeMs = Date.now() - (State.questionStartTime || Date.now());
      }
    } else {
      // 通常モードまたはアダプティブモードで通常表示の場合
      recallTimeMs = undefined;
      answerTimeMs = Date.now() - (State.questionStartTime || Date.now());
    }

    const selectedOptionId = parseInt(
      selectedRadio.dataset.optionId || "0",
      10
    );

    const isCorrect = selectedRadio.value === question.correctAnswer;
    State.lastAnswerResult = isCorrect;

    // 正解/不正解のフィードバックをUIに表示
    UI.displayAnswerFeedback(isCorrect);

    // 正解ならスコアを加算
    if (isCorrect) {
      State.incrementScore();
    }

    // 領域別の成績を記録
    // area_id が有効な数値であることを確認
    if (typeof question.area_id === "number") {
      if (!State.areaStats[question.area_id]) {
        State.areaStats[question.area_id] = { total: 0, correct: 0 };
      }
      State.areaStats[question.area_id].total++;
      if (isCorrect) {
        State.areaStats[question.area_id].correct++;
      }
    }

    // APIに送信するペイロードを作成
    const payload: ApiPayload = {
      questionId: question.id,
      selectedOptionId: selectedOptionId,
      answerTimeMs: answerTimeMs,
      recallTimeMs: recallTimeMs,
      quizMode: State.selectedQuizMode,
      displayModeUsed:
        State.currentQuestionDisplayMode || State.selectedQuizMode,
      sessionId: State.sessionId || undefined,
    };

    // 解答結果をバックグラウンドでサーバーに記録
    Api.recordSingleAnswer(payload)
      .then((response: ApiResponse) => {
        console.log("解答が正常に記録されました:", response);
      })
      .catch((error: unknown) => {
        console.error("解答の記録に失敗しました:", error);
      });
  },

  /**
   * 次の問題を読み込んで表示します。
   */
  loadNextQuestion: function (): void {
    State.incrementQuestionIndex();
    const question = State.getCurrentQuestion();
    if (!question) {
      App.displayResults();
      return;
    }

    let finalDisplayMode: string;
    if (State.selectedQuizMode === "adaptive") {
      finalDisplayMode = question.display_mode || "normal";
    } else {
      finalDisplayMode = State.selectedQuizMode;
    }
    State.currentQuestionDisplayMode = finalDisplayMode;
    UI.displayQuestion(finalDisplayMode);
  },

  /**
   * 最終的な解答結果画面を表示します。
   * チャートの描画は、画面遷移のアニメーションを妨げないよう少し遅延させます。
   */
  displayResults: function (): void {
    const accuracy =
      State.currentQuestions.length > 0
        ? ((State.score / State.currentQuestions.length) * 100).toFixed(1)
        : "0";
    UI.elements.scoreText.textContent = `スコア: ${State.score} / ${State.currentQuestions.length}`;
    UI.elements.accuracyText.textContent = `正解率: ${accuracy}%`;
    UI.showArea("result");

    // 画面描画が完了した後にチャートを描画することで、遷移をスムーズに見せる
    setTimeout(() => {
      UI.renderResultCharts();
    }, 50);
  },

  /**
   * クイズをリスタートし、試験選択画面に戻ります。
   * プリフェッチされたデータもクリアします。
   */
  restartQuiz: function (): void {
    State.prefetchedQuestions = {};
    State.resetQuizState();
    this.loadAndDisplayExamSelection();
  },

  /**
   * 指定された試験の学習履歴をリセットするフローを開始します。
   * @param {ExamData} examData - リセット対象の試験データ。
   */
  confirmAndResetHistory: function (examData: ExamData): void {
    if (!State.currentUser) {
      UI.showCustomInfoDialog(
        "ログインが必要です",
        "学習履歴をリセットするには、Googleアカウントでログインしてください。"
      );
      return;
    }
    const examDisplayText = `${examData.vendor || ""} - ${
      examData.exam_name || "不明な試験"
    }`;
    UI.showCustomConfirm(
      "学習履歴のリセット確認",
      `「${examDisplayText}」の学習履歴を本当にリセットしますか？ この操作は元に戻せません。`,
      async () => {
        UI.hideCustomConfirm();
        UI.showLoading(true);

        try {
          const response = await Api.resetExamHistory(
            examData.vendor,
            examData.exam_name
          );
          UI.showLoading(false);
          // リセット成功後、情報を表示し、OKを押したらページをリロードして最新の状態を反映
          UI.showCustomInfoDialog(
            "リセット完了",
            response.message || "リセットが完了しました。",
            () => {
              UI.showLoading(true);
              location.reload();
            }
          );
        } catch (error: unknown) {
          UI.showLoading(false);
          this.handleError(
            error,
            `「${examDisplayText}」の学習履歴リセットに失敗しました`
          );
        }
      }
    );
    if (UI.elements.customConfirmOkBtn) {
      UI.elements.customConfirmOkBtn.textContent = "リセット";
    }
  },

  // --- 以下、イベントハンドラ --- //

  /**
   * 問題数の選択が変更された際のハンドラ。
   * PC用ラジオボタンとスマホ用ドロップダウンの表示を同期させます。
   * @param {Event} e - changeイベントオブジェクト。
   */
  handleQuizLengthChange: function (e: Event): void {
    const target = e.target as HTMLInputElement;
    const newLength = parseInt(target.value, 10);
    State.selectedQuizLength = newLength;
    // PCとスマホの表示を同期
    if (
      (e.currentTarget as HTMLElement).id ===
      Constants.ELEMENT_IDS.QUIZ_LENGTH_SELECT
    ) {
      const radio = UI.elements.quizLengthOptionsContainer.querySelector(
        `input[value="${newLength}"]`
      ) as HTMLInputElement | null;
      if (radio) radio.checked = true;
    } else {
      if (UI.elements.quizLengthSelect) {
        (UI.elements.quizLengthSelect as HTMLSelectElement).value =
          newLength.toString();
      }
    }
  },

  /**
   * 問題抽出アルゴリズムの選択が変更された際のハンドラ。
   * PC用ラジオボタンとスマホ用ドロップダウンの表示を同期させます。
   * @param {Event} e - changeイベントオブジェクト。
   */
  handleQuizAlgorithmChange: function (e: Event): void {
    const target = e.target as HTMLInputElement;
    const newAlgorithm = target.value;
    State.selectedQuizAlgorithm = newAlgorithm;
    // PCとスマホの表示を同期
    if (
      (e.currentTarget as HTMLElement).id ===
      Constants.ELEMENT_IDS.QUIZ_ALGORITHM_SELECT
    ) {
      const radio = UI.elements.quizAlgorithmOptionsContainer.querySelector(
        `input[value="${newAlgorithm}"]`
      ) as HTMLInputElement | null;
      if (radio) radio.checked = true;
    } else {
      if (UI.elements.quizAlgorithmSelect) {
        (UI.elements.quizAlgorithmSelect as HTMLSelectElement).value =
          newAlgorithm;
      }
    }
  },

  /**
   * 出題モードの選択が変更された際のハンドラ。
   * PC用ラジオボタンとスマホ用ドロップダウンの表示を同期させます。
   * @param {Event} e - changeイベントオブジェクト。
   */
  handleQuizModeChange: function (e: Event): void {
    const target = e.target as HTMLInputElement;
    const newMode = target.value;
    State.selectedQuizMode = newMode;
    // PCとスマホの表示を同期
    if (
      (e.currentTarget as HTMLElement).id ===
      Constants.ELEMENT_IDS.QUIZ_MODE_SELECT
    ) {
      const radio = UI.elements.quizModeOptionsContainer.querySelector(
        `input[value="${newMode}"]`
      ) as HTMLInputElement | null;
      if (radio) radio.checked = true;
    } else {
      if (UI.elements.quizModeSelect) {
        (UI.elements.quizModeSelect as HTMLSelectElement).value = newMode;
      }
    }
  },

  /**
   * 選択肢がクリックされた際のハンドラ。
   * 選択肢のどこをクリックしてもラジオボタンが選択されるようにします。
   * @param {Event} event - clickイベントオブジェクト。
   */
  handleOptionClick: function (event: Event): void {
    const optionItem = (event.target as HTMLElement).closest(".option-item");
    if (
      optionItem &&
      !optionItem.classList.contains(Constants.CSS_CLASSES.DISABLED_OPTION)
    ) {
      const radio = optionItem.querySelector(
        'input[type="radio"]'
      ) as HTMLInputElement | null;
      if (radio && !radio.disabled) {
        radio.checked = true;
      }
    }
  },

  /** カスタムダイアログのOKボタンが押された際のハンドラ。 */
  handleDialogOk: function (): void {
    if (State._currentDialogOkCallback) {
      State._currentDialogOkCallback();
    }
    UI.hideCustomConfirm();
  },

  /** カスタムダイアログのキャンセルボタンが押された際のハンドラ。 */
  handleDialogCancel: function (): void {
    if (State._currentDialogCancelCallback) {
      State._currentDialogCancelCallback();
    }
    UI.hideCustomConfirm();
  },

  /**
   * 試験選択アコーディオンがクリックされた際のハンドラ(イベント委譲)。
   * クリックされた要素に応じて、パネルの開閉、クイズ開始、履歴リセットを振り分けます。
   * @param {Event} event - clickイベントオブジェクト。
   */
  handleAccordionClick: function (event: Event): void {
    console.log("[DEBUG] handleAccordionClick target:", event.target);
    const target = event.target as HTMLElement;
    const header = target.closest(".accordion-header");
    const startBtn = target.closest(".start-quiz-btn");
    const resetBtn = target.closest(".reset-history-btn");
    const selectAreasBtn = target.closest(".select-areas-btn");

    console.log("[DEBUG] Found elements:", {
      header,
      startBtn,
      resetBtn,
      selectAreasBtn,
    });

    if (startBtn) {
      event.stopPropagation(); // イベントの伝播を停止
      const examKey = (startBtn as HTMLElement).dataset.examKey;
      if (examKey) {
        const examData = UI._examDataCache[examKey];
        if (examData) {
          State.currentExamData = examData;
          this.startQuiz(examData.examId);
        }
      }
      return;
    }

    if (resetBtn) {
      event.stopPropagation(); // イベントの伝播を停止
      const examKey = (resetBtn as HTMLElement).dataset.examKey;
      if (examKey) {
        const examData = UI._examDataCache[examKey];
        if (examData) this.confirmAndResetHistory(examData);
      }
      return;
    }

    if (selectAreasBtn) {
      const examKey = (selectAreasBtn as HTMLElement).dataset.examKey;
      if (examKey) {
        const examData = UI._examDataCache[examKey];
        if (examData && examData.availableAreas) {
          UI.showAreaSelectionDialog(
            "学習する領域を選択",
            examData.availableAreas,
            State.selectedAreaIds,
            (newSelection) => {
              State.selectedAreaIds = newSelection;
              const buttonText = selectAreasBtn.querySelector(
                ".select-areas-text"
              ) as HTMLSpanElement;
              if (buttonText) {
                if (newSelection.length === 0) {
                  buttonText.textContent = "すべての領域";
                } else {
                  buttonText.textContent = `${newSelection.length}/${examData.availableAreas.length}領域 選択中`;
                }
              }
              UI.hideCustomConfirm();
            },
            () => UI.hideCustomConfirm()
          );
        }
      }
      event.stopPropagation(); // クリックイベントの伝播を停止
      return;
    }
    if (header) {
      UI.toggleAccordionPanel(header as HTMLElement);
    }
  },

  /**
   * フッターに現在の年を動的に設定します。
   */
  setFooterYear: function (): void {
    const yearSpans = document.querySelectorAll(".footer-year");
    if (yearSpans) {
      const currentYear = new Date().getFullYear();
      yearSpans.forEach((span) => {
        span.textContent = currentYear.toString();
      });
    }
  },

  /**
   * 設定メニューの「問題編集」がクリックされた際のハンドラ。
   */
  handleEditQuestionClick: async function (): Promise<void> {
    const question = State.getCurrentQuestion();
    if (!question) return;

    // 設定メニューを閉じる
    UI.toggleElementVisibility(UI.elements.settingsMenu, false);

    // 1. まず現在のフロント側のデータで即座にモーダルを表示 (爆速体験)
    const initialEditData: QuestionEditData = {
      id: question.id,
      questionText: question.questionText || "",
      overallExplanation: question.overallExplanation || "",
      areaId: question.area_id,
      areaName: question.area_name || null,
      importance: question.importance,
      difficulty: question.difficulty,
      tags: question.tags || [],
      options: (question.options || []).map((o) => ({
        id: o.id,
        text: o.text || "",
        explanation: o.explanation || null,
        isCorrect: o.text === question.correctAnswer,
      })),
      availableAreas: State.currentExamAreas, // キャッシュ済みの領域リストを使用
    };

    State.editingQuestionId = question.id;
    UI.showEditQuestionModal(
      initialEditData,
      this.saveEditedQuestion.bind(this),
      () => {
        State.editingQuestionId = null;
        UI.hideCustomConfirm();
        // キャンセル時、確実に解説画面に戻る
        UI.showArea("quiz");
        if (State.lastAnswerResult !== null) {
          UI.displayAnswerFeedback(State.lastAnswerResult);
        }
      }
    );

    // 2. 裏側で最新データを取得し、もし差異があれば反映 (複数人編集対策)
    try {
      const latestData = await Api.getQuestionDataForEdit(question.id);
      // キャッシュを更新
      State.currentExamAreas = latestData.availableAreas;
      // モーダルがまだ開いていれば、最新データで再構築 (サイレント更新)
      if (State.editingQuestionId === question.id) {
        UI.showEditQuestionModal(
          latestData,
          this.saveEditedQuestion.bind(this),
          () => {
            State.editingQuestionId = null;
            UI.hideCustomConfirm();
            UI.showArea("quiz");
            if (State.lastAnswerResult !== null) {
              UI.displayAnswerFeedback(State.lastAnswerResult);
            }
          }
        );
      }
    } catch (error: unknown) {
      console.warn("[編集データ同期] 最新データの取得に失敗しました:", error);
    }
  },

  /**
   * 編集された問題データを保存します。
   * @param {UpdateQuestionRequest} updatedData - 更新された問題データ。
   */
  saveEditedQuestion: async function (
    updatedData: UpdateQuestionRequest
  ): Promise<void> {
    if (!State.editingQuestionId) return;

    UI.showLoading(true);
    try {
      const response = await Api.updateQuestionData(
        State.editingQuestionId,
        updatedData
      );

      // 問題修正内容をコンソールに記録
      console.log("[問題修正記録]", {
        questionId: State.editingQuestionId,
        updatedContent: updatedData,
        timestamp: new Date().toISOString(),
      });

      // State内の現在の問題を更新して、UIを即座に反映できるようにする
      const currentQuestion = State.getCurrentQuestion();
      if (currentQuestion && currentQuestion.id === State.editingQuestionId) {
        if (updatedData.questionText !== undefined)
          currentQuestion.questionText = updatedData.questionText;
        if (updatedData.overallExplanation !== undefined)
          currentQuestion.overallExplanation = updatedData.overallExplanation;
        if (updatedData.difficulty !== undefined)
          currentQuestion.difficulty = updatedData.difficulty || 1;
        if (updatedData.importance !== undefined)
          currentQuestion.importance = updatedData.importance || 1;
        if (updatedData.tags !== undefined)
          currentQuestion.tags = updatedData.tags;
        if (updatedData.areaName !== undefined)
          currentQuestion.area_name = updatedData.areaName || "";

        // 選択肢の更新
        if (updatedData.options && currentQuestion.options) {
          updatedData.options.forEach((updatedOpt) => {
            const opt = currentQuestion.options!.find(
              (o) => o.id === updatedOpt.id
            );
            if (opt) {
              opt.text = updatedOpt.text;
              opt.explanation = updatedOpt.explanation || undefined;
            }

            // State.shuffledOptionDetails も更新
            const shuffledOpt = State.shuffledOptionDetails.find(
              (o) => o.id === updatedOpt.id
            );
            if (shuffledOpt) {
              shuffledOpt.text = updatedOpt.text;
              shuffledOpt.explanation = updatedOpt.explanation || undefined;
            }
          });
          // 正解テキストの更新
          const correctOpt = updatedData.options.find((o) => o.isCorrect);
          if (correctOpt) {
            currentQuestion.correctAnswer = correctOpt.text;
          }
        }
      }

      UI.showCustomInfoDialog(
        "保存完了",
        response.message || "問題を更新しました。",
        () => {
          State.editingQuestionId = null;

          // 確実にクイズ画面を表示し、最新データで解説画面を再描画する
          UI.showArea("quiz");
          if (currentQuestion) {
            // UI要素のテキストを直接更新
            UI.elements.questionText.textContent = currentQuestion.questionText;
            UI.elements.overallExplanation.textContent =
              currentQuestion.overallExplanation || "";

            // メタデータ（難易度、重要度、タグ等）の表示を更新
            UI.displayQuestionInfo(currentQuestion);

            // 選択肢のテキストとラジオボタンの値を同期
            const optionItems =
              UI.elements.optionsList.querySelectorAll(".option-item");
            optionItems.forEach((item, idx) => {
              const optData = State.shuffledOptionDetails[idx];
              if (optData) {
                const textEl = item.querySelector(".option-text");
                if (textEl) textEl.textContent = optData.text;

                const radio = item.querySelector(
                  'input[type="radio"]'
                ) as HTMLInputElement;
                if (radio) radio.value = optData.text;

                const explEl = item.querySelector(
                  `.${Constants.CSS_CLASSES.OPTION_SPECIFIC_EXPLANATION}`
                ) as HTMLElement;
                if (explEl) explEl.textContent = optData.explanation || "";
              }
            });

            // 正誤判定を再計算
            const selectedRadio = UI.elements.optionsList.querySelector(
              'input[name="quiz_option"]:checked'
            ) as HTMLInputElement | null;
            const newIsCorrect = selectedRadio
              ? selectedRadio.value === currentQuestion.correctAnswer
              : false;
            State.lastAnswerResult = newIsCorrect;

            UI.displayAnswerFeedback(newIsCorrect);
          }
        }
      );
    } catch (error: unknown) {
      // 編集中のエラーはモーダル内に表示し、モーダルを閉じない
      let message = "問題の更新に失敗しました";
      if (isAppError(error)) {
        message = error.message;
        if (error.status === 403) {
          message = "権限がありません。更新できません。";
        }
      } else if (error instanceof Error) {
        message = error.message;
      }
      UI.showEditError(message);
    } finally {
      UI.showLoading(false);
    }
  },

  /**
   * アプリケーション全体のエラーハンドラ。
   * @param {unknown} error - 発生したエラーオブジェクト。
   * @param {string} [contextMessage="エラーが発生しました"] - エラー発生状況のメッセージ。
   */
  handleError: function (
    error: unknown | AppError,
    contextMessage: string = "エラーが発生しました"
  ): void {
    console.error(contextMessage, error);
    UI.showLoading(false);

    let displayMessage: string;
    let status: number | undefined;

    if (isAppError(error)) {
      // カスタムAPIエラー
      displayMessage = error.message;
      if (typeof error.status === "number") {
        status = error.status;
      }
    } else if (error instanceof Error) {
      // 一般的なネットワークエラーなど
      displayMessage =
        "サーバーとの通信に問題が発生しました。時間をおいて再度お試しください。";
    } else {
      // 予期せぬエラー
      displayMessage = "予期せぬエラーが発生しました。";
    }

    // 認証エラー(401)の場合の特別な処理
    if (status === 401) {
      UI.showCustomInfoDialog(
        "エラー",
        `${contextMessage}: ${displayMessage}`,
        () => {
          // ダイアログを閉じた後、ログアウト処理を呼び出してログイン画面に遷移させる
          this.handleLogout();
        }
      );
    } else {
      // それ以外のエラーの場合の処理
      UI.showCustomInfoDialog(
        "エラー",
        `${contextMessage}: ${displayMessage}`,
        () => {
          // エラーダイアログを閉じた後、クイズ画面にいたら試験選択画面に戻す
          if (
            UI.elements.examSelectionArea &&
            UI.elements.examSelectionArea.classList.contains(
              Constants.CSS_CLASSES.HIDDEN
            )
          ) {
            this.restartQuiz();
          }
        }
      );
    }
  },
};

// DOMの読み込みが完了したらアプリケーションを初期化
document.addEventListener("DOMContentLoaded", () => {
  App.init();
});
